import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { DateTime } from 'luxon';
import { forkJoin, from, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';

import { getAllAutomaticPayments } from '@amfam/billing/auto-pay/data-access/lib/+state/auto-pay.actions';
import { getPaymentMethods } from '@amfam/billing/payment-method/data-access/lib/+state/payment-method/payment-method.actions';
import { PaymentConfirmationLoad } from '@amfam/billing/paymentconfirmation/data-access/lib/+state/paymentConfirmation.actions';
import { PendingRegistrationsFail } from '@amfam/billing/registration/data-access/lib/+state/registration.actions';
import { scheduledPaymentsLoad } from '@amfam/billing/schedulepayment/data-access/lib/+state/schedulepayment.actions';
import { BillAccountTransmuteService } from '@amfam/billing/shared/util/lib/services/bill-account-transmute.service';
import { PoliciesLoadComplete } from '@amfam/policy/data-access/lib/+state/policies/policies.actions';
import {
  selectActivePolicies,
  selectPolicyEntities,
  selectPolicyRisks
} from '@amfam/policy/data-access/lib/+state/policies/policies.selectors';
import { PolicyTypeDisplayNameConstants } from '@amfam/policy/models/lib/models/policy-type-display-name-constants';
import { PolicyTypeIconConstants } from '@amfam/policy/models/lib/models/policy-type-icon-constants';
import { AnalyticsFacade } from '@amfam/shared/analytics/lib/+state/analytics.facade';
import {
  PolicyType,
  type BillAccount,
  type DecoratedPreference,
  type DeletePreferencesStoreModel,
  type PolicyListItem,
  type PopulatePreferenceModel,
  type RetrievePreferencePayload,
  type UnassociatedRiskDetailModel,
  type UpdateAmdModel,
  type UpdateRegistrationStoreModel
} from '@amfam/shared/models/lib/billing/bill-account.model';
import {
  BillingPaymentPaths,
  INVALID_IMPERSONATION_STATUS_CODE,
  ONLINE_BILLING,
  PAPER
} from '@amfam/shared/models/lib/billing/billing-constants';
import type { PaymentConfirmationModel } from '@amfam/shared/models/lib/billing/payment-confirmation.model';
import { PcmAnalytics } from '@amfam/shared/models/lib/profile/profile-constants';
import { fromRouterActions } from '@amfam/shared/utility/navigation/lib/+state/router-actions';
import { UtilService } from '@amfam/shared/utility/shared-services/lib/core/util.service';
import { uniqBy } from '@amfam/shared/utility/shared-services/lib/utils/array.utils';

import { RegisterBillAccModel } from '../models/billingAccount.model';
import { BillAccountsService } from '../services/bill-accounts.service';
import { BillingUtilService } from '../services/billing-util.service';
import {
  billAccountDeletePreference,
  billAccountDeletePreferenceFail,
  billAccountDeletePreferenceSuccess,
  billAccountLoadDetail,
  billAccountLoadDetailFail,
  billAccountLoadDetailSuccess,
  billAccountLoadDocuments,
  billAccountLoadDocumentsFail,
  billAccountLoadDocumentsSuccess,
  billAccountLoadPreference,
  billAccountLoadPreferenceFail,
  billAccountLoadPreferenceSuccess,
  billAccountLoadUnAssociatedRiskDetail,
  billAccountLoadUnAssociatedRiskDetailFail,
  billAccountLoadUnAssociatedRiskDetailSuccess,
  billAccountPopulatePreference,
  billAccountReloadMinimumDue,
  billAccountReloadMinimumDueFail,
  billAccountReloadMinimumDueSuccess,
  billAccountSilentRegistration,
  billAccountsLoadAfiMidvale,
  billAccountsLoadComplete,
  billAccountsLoadFail,
  billAccountsLoadSuccess,
  billAccountTransformPolicyList,
  billAccountTransformPolicyListFail,
  billAccountTransformPolicyListSuccess,
  billAccountUnregisterAssociated,
  billAccountUnregisterUnAssociated,
  billAccountUpdateBillingPreference,
  billAccountUpdateBillingPreferenceFail,
  billAccountUpdateBillingPreferenceSuccess,
  billAccountUpdateDeliveryPreference,
  billAccountUpdateDeliveryPreferenceFail,
  billAccountUpdateDeliveryPreferenceSuccess,
  billAccountUpdateReadOnlyStatus,
  billAccountUpdateRegistrationFail,
  billAccountUpdateRegistrationPartialFail,
  billAccountUpdateRegistrationSubmit,
  billAccountUpdateRegistrationSuccess
} from './billaccount.actions';
import {
  selectAllBillAccounts,
  selectBillAccountEntities,
  selectBillAccounts,
  selectMatchedBillAccount
} from './billaccount.selectors';

export const loadAfiMidvale = createEffect(
  (
    actions$ = inject(Actions),
    billingService = inject(BillAccountsService),
    billingTransmuteService = inject(BillAccountTransmuteService),
    analyticsFacade = inject(AnalyticsFacade)
  ) => {
    return actions$.pipe(
      ofType(billAccountsLoadAfiMidvale),
      switchMap(() => {
        return billingService.getBillAccountsAfiMidvale().pipe(
          map(undecorated => billingTransmuteService.decorateAccountsArray(undecorated)),
          mergeMap((fetchedBillAccount: BillAccount[]) => {
            const atLeastOnePastDueAccount = fetchedBillAccount.some(acc => acc.pastDue);
            if (atLeastOnePastDueAccount) {
              analyticsFacade.trackEvent({
                event: 'pastdue_bill',
                eventStep: ''
              });
            }
            return [billAccountsLoadSuccess(fetchedBillAccount), billAccountsLoadComplete()];
          }),
          catchError(error => of(billAccountsLoadFail(error), billAccountsLoadComplete()))
        );
      })
    );
  },
  { functional: true }
);

export const loadAutoPays = createEffect(
  (actions$ = inject(Actions), store = inject(Store), utilService = inject(UtilService)) => {
    return actions$.pipe(
      ofType(billAccountsLoadComplete),
      map(action => action.payload),
      concatLatestFrom(() => store.select(selectAllBillAccounts)),
      map(([, billAccounts]) => {
        return getAllAutomaticPayments({
          billAccountNumbers: billAccounts.map(ba => ba?.billAccountNumber),
          correlationId: utilService.generateId()
        });
      })
    );
  },
  { functional: true }
);

export const loadAll = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(billAccountsLoadSuccess),
      map(action => action.payload ?? []),
      mergeMap(decoratedArray => from(decoratedArray)),
      mergeMap(ba => {
        const billAccountActions: Action[] = [];

        // Personal users have full detail (including policyList) from individual /afi/{id} calls
        // in getBillAccountsAfiMidvale. Business users only get by-waid summary (no policyList)
        // and still need a separate detail call.
        const missingDetailData = !ba.policyList;
        if (missingDetailData) {
          // Business user lands here
          // performs the transmuteDetailCall where the isAdvance is set
          billAccountActions.push(
            billAccountLoadDetail({
              billAccountNumber: ba.billAccountNumber,
              associated: ba.associated
            })
          );
        } else {
          // personal user lands here
          // Details already loaded from /afi/{id} call - dispatch success to mark detailsLoaded=true
          billAccountActions.push(billAccountLoadDetailSuccess(ba));
        }

        // With PCM changes we need to make Get billAccount/id/preferences call irrespective of registration.
        const payload: RetrievePreferencePayload = {
          billAccountNumber: ba.billAccountNumber,
          associated: ba.associated,
          billingMethod: ba.billingMethod
        };
        billAccountActions.push(billAccountLoadPreference(payload));
        return billAccountActions;
      }),
      catchError(error => of(billAccountsLoadFail(error)))
    );
  },
  { functional: true }
);

export const loadDetail = createEffect(
  (
    actions$ = inject(Actions),
    billingService = inject(BillAccountsService),
    billingTransmuteService = inject(BillAccountTransmuteService)
  ) => {
    return actions$.pipe(
      ofType(billAccountLoadDetail),
      map(action => action.payload),
      mergeMap(payload =>
        billingService.getBillAccount(payload).pipe(
          map(res => res.billingAccount),
          map(undecorated => billingTransmuteService.transmuteDetailCall(undecorated)),
          map(decorated => ({ ...decorated, associated: payload.associated })),
          map(res => {
            const billAccountsActionsArr: Action[] = [billAccountLoadDetailSuccess(res)];
            if (!res.associated && (res.policyList?.length ?? 0) > 0) {
              billAccountsActionsArr.push(
                billAccountLoadUnAssociatedRiskDetail({
                  billAccountNumber: res.billAccountNumber,
                  policyList: res.policyList ?? []
                })
              );
            }
            return billAccountsActionsArr;
          }),
          mergeMap(actions => actions),
          catchError(error =>
            of(
              billAccountLoadDetailFail({
                billAccountNumber: payload.billAccountNumber ?? '',
                status: error
              })
            )
          )
        )
      )
    );
  },
  { functional: true }
);

export const loadUnassociatedBaPolicyDetail = createEffect(
  (
    actions$ = inject(Actions),
    billingService = inject(BillAccountsService),
    store = inject(Store),
    billingUtilService = inject(BillingUtilService)
  ) => {
    return actions$.pipe(
      ofType(billAccountLoadUnAssociatedRiskDetail),
      map(action => action.payload),
      mergeMap(payload =>
        billingService.getUnAssociatedBillAccountRiskInformation(payload.policyList).pipe(
          withLatestFrom(store.select(selectMatchedBillAccount, payload.billAccountNumber)),
          map(([policyList, billAccountArr]) => {
            return {
              newPolicyList: billingUtilService.uniqPolicyList(
                policyList,
                billAccountArr[0]?.policyList ?? []
              ),
              billaccountObj: billAccountArr[0]
            };
          }),
          map(transmutedObj => {
            let responsePayload: UnassociatedRiskDetailModel = {
              billAccountNumber: payload.billAccountNumber,
              policyList: transmutedObj.newPolicyList
            };

            if (!transmutedObj.billaccountObj?.associated) {
              let icon = PolicyTypeIconConstants.UNASSOCIATED;

              const uniquePolicyList = uniqBy(
                transmutedObj.billaccountObj?.policyList ?? [],
                item => item.policyType
              );
              if (
                uniquePolicyList.length === 1 &&
                (uniquePolicyList[0]?.policyType === PolicyType.FARM_RANCH ||
                  uniquePolicyList[0]?.policyType === PolicyType.FR_UMBRELLA)
              ) {
                icon = PolicyTypeIconConstants.COMMERCIAL;
              }

              responsePayload = {
                ...responsePayload,
                icon: icon,
                policyTypeDisplayName: PolicyTypeDisplayNameConstants.UNASSOCIATED
              };
            }
            return billAccountLoadUnAssociatedRiskDetailSuccess(responsePayload);
          }),
          catchError(() =>
            of(
              billAccountLoadUnAssociatedRiskDetailFail({
                billAccountNumber: payload.billAccountNumber
              })
            )
          )
        )
      )
    );
  },
  { functional: true }
);

export const loadPrefs = createEffect(
  (
    actions$ = inject(Actions),
    billingService = inject(BillAccountsService),
    analyticsFacade = inject(AnalyticsFacade)
  ) => {
    return actions$.pipe(
      ofType(billAccountLoadPreference),
      map(action => action.payload),
      mergeMap(payload =>
        billingService.getPreferences(payload).pipe(
          map(res => billAccountLoadPreferenceSuccess(res as DecoratedPreference)),
          catchError(error => {
            analyticsFacade.trackPage(PcmAnalytics.getPreferencesPartialFailure);
            return of(
              billAccountLoadPreferenceFail({
                billAccountNumber: payload.billAccountNumber,
                status: error
              })
            );
          })
        )
      )
    );
  },
  { functional: true }
);

export const updateBillingPrefs = createEffect(
  (
    actions$ = inject(Actions),
    billingService = inject(BillAccountsService),
    analyticsFacade = inject(AnalyticsFacade)
  ) => {
    return actions$.pipe(
      ofType(billAccountUpdateBillingPreference),
      map(action => action.payload),
      mergeMap(payload =>
        billingService.updateBillingPreferences(payload).pipe(
          mergeMap(updatedBillingPreferences => {
            const updateBillingPreferencesNextActions: Action[] = [];

            if (updatedBillingPreferences) {
              // Translate the response object into the correct naming conventions (see bill-account.model.ts for details)
              const updatePreferencesResponse = {
                billAccountNumber: payload.billAccountNumber,
                updatePreferenceCorrelationId: payload.updatePreferenceCorrelationId,
                billingPreferences: {
                  accountNickName: payload.payload.billingPreference.accountNickname ?? '',
                  dueDateReminder: payload.payload.billingPreference.dueDateReminder ?? '',
                  preferences: payload.payload.billingPreference.preferences ?? []
                }
              };
              updateBillingPreferencesNextActions.push(
                billAccountUpdateBillingPreferenceSuccess(updatePreferencesResponse)
              );
            } else {
              analyticsFacade.trackPage(PcmAnalytics.viewDetailPutFailure);
              updateBillingPreferencesNextActions.push(
                billAccountUpdateBillingPreferenceFail({
                  billAccountNumber: payload.billAccountNumber,
                  updatePreferenceCorrelationId: payload.updatePreferenceCorrelationId,
                  error: updatedBillingPreferences
                })
              );
            }
            return updateBillingPreferencesNextActions;
          }),
          catchError(error => {
            analyticsFacade.trackPage(PcmAnalytics.viewDetailPutFailure);
            return of(
              billAccountUpdateBillingPreferenceFail({
                billAccountNumber: payload.billAccountNumber,
                updatePreferenceCorrelationId: payload.updatePreferenceCorrelationId,
                error: error
              })
            );
          })
        )
      )
    );
  },
  { functional: true }
);

export const updateDeliveryPrefs = createEffect(
  (actions$ = inject(Actions), billingService = inject(BillAccountsService)) => {
    return actions$.pipe(
      ofType(billAccountUpdateDeliveryPreference),
      map(action => action.payload),
      mergeMap(payload =>
        billingService.updateDeliveryPreference(payload).pipe(
          mergeMap(updatedDeliveryPreferences => {
            const updateDeliveryPreferenceNextActions: Action[] = [];

            if (updatedDeliveryPreferences) {
              // Retrieve preferences payload
              const retrievePreferencepayload: RetrievePreferencePayload = {
                billAccountNumber: payload.billAccountNumber,
                associated: payload.associated,
                billingMethod: payload.billingMethod
              };
              // Update delivery preference response
              const updatePreferencesResponse = {
                billAccountNumber: payload.billAccountNumber,
                updatePreferenceCorrelationId: payload.updatePreferenceCorrelationId
              };
              updateDeliveryPreferenceNextActions.push(
                billAccountLoadPreference(retrievePreferencepayload),
                billAccountUpdateDeliveryPreferenceSuccess(updatePreferencesResponse)
              );
            } else {
              updateDeliveryPreferenceNextActions.push(
                billAccountUpdateDeliveryPreferenceFail({
                  billAccountNumber: payload.billAccountNumber,
                  updatePreferenceCorrelationId: payload.updatePreferenceCorrelationId,
                  error: updatedDeliveryPreferences
                })
              );
            }
            return updateDeliveryPreferenceNextActions;
          }),
          catchError(error => {
            return of(
              billAccountUpdateDeliveryPreferenceFail({
                billAccountNumber: payload.billAccountNumber,
                updatePreferenceCorrelationId: payload.updatePreferenceCorrelationId,
                error: error
              })
            );
          })
        )
      )
    );
  },
  { functional: true }
);

export const deletePrefs = createEffect(
  (
    actions$ = inject(Actions),
    billingService = inject(BillAccountsService),
    analyticsFacade = inject(AnalyticsFacade)
  ) => {
    return actions$.pipe(
      ofType(billAccountDeletePreference),
      map(action => action.payload),
      switchMap(payload =>
        billingService.deletePreferences(payload.deletePreference).pipe(
          mergeMap(() => {
            // Translate the response object into the correct naming conventions (see bill-account.model.ts for details)
            const successObject = <DeletePreferencesStoreModel>{
              billAccountNumber: payload.deletePreference.billAccountNumber,
              deleteCorrelationId: payload.deletePreference.deleteCorrelationId
            };

            const confirmationPayload: PaymentConfirmationModel = {
              category: 'billAccount',
              subCategory: 'remove',
              nickName: payload.deletePreference.billAccountNumber,
              policyDescription: payload.deletePreference.policyDescription
            };

            return [
              fromRouterActions.Go({ path: [BillingPaymentPaths.CONFIRMATION_PATH] }),
              billAccountLoadPreference(payload.loadPreference),
              PaymentConfirmationLoad(confirmationPayload),
              billAccountDeletePreferenceSuccess(successObject)
            ];
          }),
          catchError(error => {
            analyticsFacade.trackPage(PcmAnalytics.unBAMDeleteFailure);
            return of(
              billAccountDeletePreferenceFail({
                billAccountNumber: payload.deletePreference.billAccountNumber,
                deleteCorrelationId: payload.deletePreference.deleteCorrelationId,
                error: error
              })
            );
          })
        )
      )
    );
  },
  { functional: true }
);

export const loadDocuments = createEffect(
  (
    actions$ = inject(Actions),
    billingService = inject(BillAccountsService),
    billingTransmuteService = inject(BillAccountTransmuteService)
  ) => {
    return actions$.pipe(
      ofType(billAccountLoadDocuments),
      map(action => action.payload),
      mergeMap(payload =>
        billingService.getStatementHistory(payload.billAccountNumber, payload.billingSystem).pipe(
          map(res => res.billAccount),
          map(undecorated => billingTransmuteService.decorateDocumentsCall(undecorated)),
          map(res => billAccountLoadDocumentsSuccess(res)),
          catchError(error =>
            of(
              billAccountLoadDocumentsFail({
                billAccountNumber: payload.billAccountNumber,
                status: error
              })
            )
          )
        )
      )
    );
  },
  { functional: true }
);

export const silentRegistration = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    billingTransmuteService = inject(BillAccountTransmuteService)
  ) => {
    return actions$.pipe(
      ofType(billAccountSilentRegistration),
      map(action => action.payload),
      concatLatestFrom(() => [
        store.select(selectBillAccountEntities),
        store.select(selectActivePolicies)
      ]),
      mergeMap(([pendingRegistrations, billAccountEntities, activePolicyList]) => {
        const registrationActions: Action[] = [];
        pendingRegistrations.forEach(item => {
          const apiPayload: RegisterBillAccModel = billingTransmuteService.buildRegistrationPayload(
            billAccountEntities[item.billAccountNumber],
            item.correlationId,
            activePolicyList
          );
          registrationActions.push(billAccountUpdateRegistrationSubmit(apiPayload));
        });
        return registrationActions;
      })
    );
  },
  { functional: true }
);

export const updateRegistration = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    billingService = inject(BillAccountsService),
    analyticsFacade = inject(AnalyticsFacade),
    utilService = inject(UtilService)
  ) => {
    return actions$.pipe(
      ofType(billAccountUpdateRegistrationSubmit),
      map(action => action.payload),
      concatLatestFrom(() => store.select(selectAllBillAccounts)),
      mergeMap(([payload, billAccounts]) =>
        billingService.updateRegistration(payload).pipe(
          mergeMap((noPartialFailure: boolean) => {
            const actionsArray: Action[] = [];
            const loadPreferencePayload: RetrievePreferencePayload = {
              billAccountNumber: payload.billAccountNumber,
              associated: payload.registrationData.associated,
              billingMethod: '' // Billing method set below based on condition
            };

            if (noPartialFailure) {
              // Success case
              const confirmationPayload = {
                category: 'billAccount',
                subCategory: payload.isUnregister ? 'remove' : 'add',
                billAccountNumber: payload.billAccountNumber,
                nickName: payload.registrationData.accountName,
                riskDescription: payload.registrationData.riskDescription,
                policyDescription: payload.registrationData.policyDescription,
                emailAddress: payload.registrationData.emailAddress,
                associated: payload.registrationData.associated
              };

              const loadAction = PaymentConfirmationLoad(confirmationPayload);

              const registrationSuccessPayload: UpdateRegistrationStoreModel = {
                billAccountNumber: payload.billAccountNumber,
                correlationId: payload.correlationId,
                billingMethod: ONLINE_BILLING,
                registrationDate: DateTime.now().toFormat('yyyy-MM-ddZ'),
                userIsBAM: true
              };

              let extraActions: Action[];

              // TODO make this more explicit for each resulting call we're making for clarity
              const actionpayload = {
                billAccountNumber: payload.billAccountNumber,
                policyNumber: payload.registrationData.policyNumber,
                associated: payload.registrationData.associated,
                billingSystem: payload.registrationData.billingSystem ?? '' // TODO: This value is never set, always caused the documents call to fail.
              };
              const preferencesPayload: PopulatePreferenceModel = {
                billAccountNumber: payload.billAccountNumber.replace(/-/g, ''),
                billingPreferences: {
                  accountNickName: payload.registrationData.accountName,
                  dueDateReminder: payload.registrationData.reminder,
                  deliveryMethod: 'ELECTRONIC'
                }
              };

              if (payload.isUnregister) {
                /*
              For an associated bill accounts, we also need to re-load the bill account details, documents and clean up the store
              For unassociated accounts, remove all of this bill account's data from the store
              For both types, refresh the payment actions, scheduled payments and autopay rules.
            */
                /*
              TODO - scheduled payments and autopay rules can probably get a
              "remove rule" action that just truncates from the store for this specific bill account.
            */
                if (payload.registrationData.associated) {
                  loadPreferencePayload.billingMethod = PAPER;
                  extraActions = [
                    billAccountLoadDocuments(actionpayload),
                    billAccountUnregisterAssociated(actionpayload),
                    scheduledPaymentsLoad()
                  ];
                } else {
                  loadPreferencePayload.billingMethod = PAPER;
                  extraActions = [
                    billAccountUnregisterUnAssociated({
                      bilLAccountNumber: actionpayload.billAccountNumber
                    }),
                    scheduledPaymentsLoad()
                  ];
                }
                extraActions.push(
                  getAllAutomaticPayments({
                    billAccountNumbers: billAccounts.map(ba => ba?.billAccountNumber),
                    correlationId: utilService.generateId()
                  })
                );
                extraActions.push(
                  getPaymentMethods({
                    correlationId: utilService.generateId()
                  })
                );
              } else {
                // If this is a simple registration fire actions to load the details, preferences and documents
                loadPreferencePayload.billingMethod = ONLINE_BILLING;
                extraActions = [
                  billAccountPopulatePreference(preferencesPayload),
                  billAccountLoadDocuments(actionpayload)
                ];
              }
              // Dispatch with conditional payload(refer to comment Update billingMethod in store)
              const successAction = billAccountUpdateRegistrationSuccess(
                registrationSuccessPayload
              );
              // set up actions array and push to it instead
              actionsArray.push(loadAction, successAction, ...extraActions);
            } else {
              // Partial fail case
              if (payload.isUnregister) {
                analyticsFacade.trackPage(PcmAnalytics.unregisterPartialFailure);
              } else {
                analyticsFacade.trackPage(PcmAnalytics.registrationPartialFailure);
              }
              loadPreferencePayload.billingMethod = PAPER;
              const updateRegistrationPartialFailAction = billAccountUpdateRegistrationPartialFail({
                billAccountNumber: payload.billAccountNumber,
                correlationId: payload.correlationId
              });
              actionsArray.push(updateRegistrationPartialFailAction);
            }
            /**
             * AS: If we are trying to unregisted an unassociated account and there are no failures
             * we should not be making the preference call.
             */
            if (noPartialFailure && payload.isUnregister && !payload.registrationData.associated) {
              return actionsArray;
            }
            actionsArray.push(billAccountLoadPreference(loadPreferencePayload));
            return actionsArray;
          }),
          catchError(error => {
            // If it's an unregister or unregister, fire the full failure analytics
            if (payload.isUnregister) {
              analyticsFacade.trackPage(PcmAnalytics.unregisterFailure);
            } else {
              analyticsFacade.trackPage(PcmAnalytics.registrationFailure);
            }

            const code = error?.messages?.[0]?.code;
            let errorText: string;
            if (code && code.toString() === INVALID_IMPERSONATION_STATUS_CODE) {
              errorText = INVALID_IMPERSONATION_STATUS_CODE;
            } else {
              errorText = !navigator.onLine ? 'Please check your internet connection.' : error;
            }
            return [
              billAccountUpdateRegistrationFail({
                billAccountNumber: payload.billAccountNumber,
                correlationId: payload.correlationId,
                error: errorText
              }),
              PendingRegistrationsFail(payload.billAccountNumber)
            ];
          })
        )
      )
    );
  },
  { functional: true }
);

/**
 * @author: Abhishek Singh
 * @description: This effect listens to the success of the update registration and subsequent
 * detail success then zips them together.
 * The purpose of the effect is to make sure we have bill account details loaded after a registration update
 * before we dispatch an action to transform the policylist associated with the bill account.
 */

export const transFormBillAccountPostRegistration = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) => {
    return actions$.pipe(
      ofType(billAccountLoadDetailSuccess),
      map(action => action.payload),
      concatLatestFrom(() => [store.select(selectPolicyRisks), store.select(selectPolicyEntities)]),
      mergeMap(([billAccountObj, policyList, policyEntities]) =>
        of(
          billAccountTransformPolicyList({
            policyList: policyList,
            billAccount: billAccountObj,
            policyEntities: policyEntities
          })
        )
      )
    );
  },
  { functional: true }
);

/**
 * @author: Abhishek Singh
 * @description: This effect listens to the success of the policy summary actions and then
 * maps to inner observable of billaccount loadDetailAction, so that every time the billing
 * details have been loaded we dispatch an action to transform the policy list associated with
 * that billaccount.
 * The purpose of the effect is to make sure we have policies and bill account details loaded
 * before we dispatch an action to transform the policylist associated with the bill account.
 */

export const policyCompleteAction = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) => {
    return actions$.pipe(
      ofType(PoliciesLoadComplete),
      concatLatestFrom(() => [
        store.select(selectBillAccounts),
        store.select(selectPolicyRisks),
        store.select(selectPolicyEntities)
      ]),
      mergeMap(([, billingList = [], policyList, policyEntities]) => {
        return from(billingList).pipe(
          mergeMap(billaccount =>
            of(
              billAccountTransformPolicyList({
                policyList: policyList,
                billAccount: <BillAccount>billaccount,
                policyEntities: policyEntities
              })
            )
          )
        );
      }),
      catchError(error => {
        return of(billAccountTransformPolicyListFail(error));
      })
    );
  },
  { functional: true }
);

/**
 * @author: Abhishek Singh
 * @description: This effect listens to the transform policy action and call a function which tranforms
 * the policy list associated with a bill account to a policy list which has risk description in detail.
 */

export const transformPolicyListAction = createEffect(
  (actions$ = inject(Actions), billingTransmuteService = inject(BillAccountTransmuteService)) => {
    return actions$.pipe(
      ofType(billAccountTransformPolicyList),
      map(action => action.payload),
      mergeMap(response =>
        billingTransmuteService
          .transformBillingPolicyList(response.billAccount.policyList, response.policyList)
          .pipe(
            switchMap(transformedList => {
              const transFormedIconPayload = billingTransmuteService.getIcon(
                response.billAccount,
                response.policyList,
                response.policyEntities
              );
              return of(
                billAccountTransformPolicyListSuccess({
                  billAccount: {
                    ...response.billAccount,
                    policyList: transformedList,
                    icon: transFormedIconPayload.icon,
                    policyTypeDisplayName: transFormedIconPayload.policyTypeDisplayName ?? ''
                  }
                })
              );
            }),
            catchError(error => of(billAccountTransformPolicyListFail(error)))
          )
      ),
      catchError(error => of(billAccountTransformPolicyListFail(error)))
    );
  },
  { functional: true }
);

/**
 * @author: Abhishek Singh
 * @description: This effect listens to the transform policy action and checks if the incoming
 * billaccount has a policy which the user is not a named insured on. In that case we call payyorobo
 * to provide the policy description.
 */

export const loadUnassociatedPolicyListAction = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(billAccountTransformPolicyList),
      map(action => action.payload),
      mergeMap(response => {
        const actionArr: Action[] = [];

        const policyForPayorObo: PolicyListItem[] = [];
        (response.billAccount?.policyList ?? []).forEach(policyFromBillObj => {
          const policyExist = response.policyList.find(policyItem =>
            policyFromBillObj.policyNumber?.includes(policyItem.policyNumber)
          );

          if (!policyExist) {
            policyForPayorObo.push(policyFromBillObj);
          }
        });

        if (policyForPayorObo.length > 0) {
          actionArr.push(
            billAccountLoadUnAssociatedRiskDetail({
              billAccountNumber: response.billAccount.billAccountNumber,
              policyList: policyForPayorObo
            })
          );
        }
        return actionArr;
      })
    );
  },
  { functional: true }
);

export const UpdateReadOnlyStatus = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(
        billAccountLoadPreferenceSuccess,
        billAccountLoadPreferenceFail,
        billAccountDeletePreferenceFail
      ),
      map(action => action.payload),
      mergeMap(payload => [billAccountUpdateReadOnlyStatus(payload as DecoratedPreference)])
    );
  },
  { functional: true }
);

export const updateAmd = createEffect(
  (
    actions$ = inject(Actions),
    billingService = inject(BillAccountsService),
    billingTransmuteService = inject(BillAccountTransmuteService)
  ) => {
    return actions$.pipe(
      ofType(billAccountReloadMinimumDue),
      map(action => action.payload),
      mergeMap((payload: string[]) => {
        // Build an array of observables of GET /billaccounts/{ID}
        const requestArray = payload.map((billAccountNumber: string) => {
          // Call GET /billaccounts/{ID}
          return billingService.getBillAccount({ billAccountNumber: billAccountNumber }).pipe(
            map(response => {
              const transmuted = billingTransmuteService.transmuteDetailCall(
                response.billingAccount
              );
              return {
                billAccountNumber: String(response.billingAccount.billAccountNumber),
                minimumAmountDue: Number(response.billingAccount.minimumAmountDue),
                pastDue: transmuted.pastDue
              };
            })
          );
        });
        // Fork Join the observables - we only want to emit if all are successful
        return forkJoin(requestArray).pipe(
          mergeMap((updates: UpdateAmdModel[]) => {
            const updateActions: Action[] = [];
            if (updates && updates.length) {
              updates.forEach((update: UpdateAmdModel) => {
                if (update.billAccountNumber && update.minimumAmountDue != null) {
                  updateActions.push(billAccountReloadMinimumDueSuccess(update));
                }
              });
            }
            return updateActions;
          }),
          catchError(error => of(billAccountReloadMinimumDueFail({ status: error })))
        );
      })
    );
  },
  { functional: true }
);
